import React, { useEffect, useState } from 'react';
import { Alert, UncontrolledTooltip } from 'reactstrap';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { hideToast } from '../../actions/toast';
import { devices } from '../../constants/Devices';
import {
  TOAST_CONFIG,
  DEFAULT_TOAST_CONFIG,
} from '../../constants/ToastConfig';
import IMAGES from '../../constants/Images';

const ToastContainer = styled.section`
  position: fixed;
  z-index: 1999;

  /* Default positioning (bottom-left) */
  bottom: -10px;

  @media ${devices.md} {
    bottom: 40px;
    left: 40px;
  }

  /* Dynamic positioning based on toast type */
  ${props => {
    switch (props.position) {
      case 'top-right':
        return `
          top: 20px;
          right: 20px;
          bottom: auto;
          left: auto;
          
          @media ${devices.md} {
            top: 20px;
            right: 20px;
            bottom: auto;
            left: auto;
          }
        `;
      case 'top-left':
        return `
          top: 20px;
          left: 20px;
          bottom: auto;
          right: auto;
          
          @media ${devices.md} {
            top: 20px;
            left: 20px;
            bottom: auto;
            right: auto;
          }
        `;
      case 'bottom-right':
        return `
          bottom: 20px;
          right: 20px;
          top: auto;
          left: auto;
          
          @media ${devices.md} {
            bottom: 40px;
            right: 20px;
            top: auto;
            left: auto;
          }
        `;
      case 'bottom-left':
      default:
        return '';
    }
  }}
  .alert {
    display: flex;
    max-width: 400px;
    min-width: 200px;
    padding-right: 50px;
    .alert-inner--text {
      max-width: 400px;
      a {
        color: #10112e;
        font-weight: 500;
        text-decoration: underline;
      }
    }
    .close {
      top: 16px;
      right: 8px;
    }
  }
  .alert.alert-danger {
    background-color: #f5365c;
  }
  .alert.alert-success {
    background-color: #2dce89;
  }
  .alert.alert-voice-detected {
    background-color: #f5365c;
  }
  .alert-inner--icon {
    margin-top: -4px;
    margin-right: 16px;

    .microphone-svg {
      width: 20px;
      height: 20px;
      filter: brightness(0) invert(1); /* Makes the SVG white */
    }
  }
  .error-text-bold {
    font-weight: 600;
  }
`;
const ErrorCodeSpan = styled.span`
  margin-left: 8px;
  opacity: 0.8;
`;

const Link = styled.a`
  cursor: pointer;
  text-decoration: underline !important;
`;

export default () => {
  const { toast } = useSelector(state => state.appStatus);
  const { show, type, message, apiErrorInfo } = toast;
  const [tooltipText, setTooltipText] = useState('');
  const [isTooltipOpen, setTooltipOpen] = useState(false);

  const dispatch = useDispatch();
  useEffect(() => {
    if (show && !apiErrorInfo?.delay) {
      setTimeout(() => {
        dispatch(hideToast());
      }, 5000);
    }
    if (apiErrorInfo && apiErrorInfo.copyText) {
      setTooltipText(`Click here to copy ${apiErrorInfo.copyText}`);
    }
  }, [dispatch, show]);

  const toggle = () => {
    dispatch(hideToast());
  };
  // Get toast configuration based on type
  const toastConfig = TOAST_CONFIG[type] || DEFAULT_TOAST_CONFIG;
  const iconClass = toastConfig.icon;
  const isVoiceDetected = toastConfig.customLayout || false;

  const formatApiErrorMessage = (label, value) => {
    if (!value) {
      return label;
    }
    return (
      <span>
        {label.split(value).reduce((prev, current, i) => {
          if (!i) {
            return [current];
          }
          return prev.concat(
            <Link
              id="tooltip-link"
              onClick={e => {
                e.preventDefault();
                navigator.clipboard.writeText(apiErrorInfo.copyText);
                setTooltipText(`Copied ${apiErrorInfo.copyText}`);
              }}
            >
              {value}
            </Link>,
            current
          );
        }, [])}
      </span>
    );
  };

  return (
    <ToastContainer position={toastConfig.position}>
      <Alert color={toastConfig.color} isOpen={show} toggle={toggle}>
        <span className="alert-inner--icon">
          {isVoiceDetected ? (
            <img
              src={IMAGES.MIC_NOT_FOUND}
              alt="Microphone"
              className="microphone-svg"
            />
          ) : (
            <i className={`fa ${iconClass}`} />
          )}
        </span>
        &nbsp;
        {isVoiceDetected ? (
          <span className="alert-inner--text">
            <strong>{toastConfig.title}</strong>
            <br />
            {toastConfig.message}
          </span>
        ) : (
          <>
            {!apiErrorInfo?.message && (
              <span
                className="alert-inner--text"
                dangerouslySetInnerHTML={{ __html: message }}
              />
            )}
            {apiErrorInfo && (
              <>
                {apiErrorInfo.copyText && (
                  <UncontrolledTooltip
                    placement="top"
                    target="tooltip-link"
                    isOpen={isTooltipOpen}
                    toggle={() => {
                      if (apiErrorInfo && apiErrorInfo.copyText) {
                        setTooltipText(
                          `Click here to copy ${apiErrorInfo.copyText}`
                        );
                      }
                      setTooltipOpen(data => !data);
                    }}
                  >
                    {tooltipText}
                  </UncontrolledTooltip>
                )}
                <span className="error-text-bold">
                  {apiErrorInfo.copyText
                    ? formatApiErrorMessage(message, apiErrorInfo.copyText)
                    : message}
                  {apiErrorInfo.errorCode && (
                    <ErrorCodeSpan>({apiErrorInfo.errorCode})</ErrorCodeSpan>
                  )}
                </span>
              </>
            )}
          </>
        )}
      </Alert>
    </ToastContainer>
  );
};
